import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import EditAccount from "../../components/account/EditAccount/EditAccount";
import AmpButton from "../../components/common/AmpButton/AmpButton";
import AmpLoadingSpinner from "../../components/common/AmpLoadingSpinner/AmpLoadingSpinner";
import BlueBoxTitle from "../../components/common/BlueBoxTitle/BlueBoxTitle.module";
import PropertySelectionList from "../../components/property/PropertySelectionList/PropertySelectionList";
import { getUserInfo } from "../../redux/actions/auth/manageUser";
import { getSecurityQuestion } from "../../redux/actions/auth/securityQuestion";
import { changeIsCloseable, changeModalContent, changeModalState } from "../../redux/actions/modal/modal";
import { setToastNotification } from "../../redux/actions/toastNotification/toastNotification";
import { RootState } from "../../redux/reducers/root";

import "./ManageMyAccount.css";
import EditEnotifcation from "../../components/account/Enotification/Edit";
import { UserData } from "../../redux/reducers/getAuthReducer/getUserInfoReducer";

export default function ManageMyAccount() {

	const { t } = useTranslation(['profile', "login"]);

	const navigate = useNavigate();

	const dispatch = useDispatch();

	const manageUserInfoState = useSelector((state: RootState) => state.authReducer.getUser as UserData);
	const securityQuestionState: Record<string, unknown> = useSelector((state: RootState) => state.authReducer.getSecurityQuestion);
	const userUpdateState: Record<string, unknown> = useSelector((state: RootState) => state.authReducer.updateUser);

	const modalState = useSelector((state: RootState) => state.modalReducer);

	const [userEmail, setUserEmail] = useState("");
	const [userSecurityQuestion, setUserSecurityQuestion] = useState("");
	const [userLanguage, setUserLanguage] = useState("");

	const userDetailsLoading = manageUserInfoState.loading as boolean;
	const securityQuestionLoading = securityQuestionState.loading as boolean;

	const isAgent = window.localStorage.getItem("isAgent") === "Y";

	const modalContentAriaLabel: string = modalState['contentAriaLabel'];
	const modalIsCloseable: boolean = modalState['isCloseable'];

	useEffect(() => {
		if (!manageUserInfoState.user_profile_id) {
			const userProfileId = window.localStorage.getItem("userProfileId");
			if (userProfileId) {
				dispatch(getUserInfo(userProfileId));
				dispatch(getSecurityQuestion(userProfileId));
			} else {
				dispatch(setToastNotification("Not allowed to access this resource", "error"));
			}
		} else if (!securityQuestionState.question_text) {
			dispatch(getSecurityQuestion(manageUserInfoState.user_profile_id as string));
		}
		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		if (manageUserInfoState.status && manageUserInfoState.status !== 200) {
			dispatch(setToastNotification(manageUserInfoState.message as string ?? "Not allowed to access this resource", "error"));
		} else {
			setUserEmail(manageUserInfoState.user_id as string);
			setUserLanguage(manageUserInfoState.pref_lang as string);
		}
	}, [manageUserInfoState, dispatch]);

	useEffect(() => {

		const optionArray = {
			1: t('3-what-childhood-nick', {ns:"login"}),
			2: t('3-what-childhood-friend', {ns:"login"}),
			3: t('3-city-job-q', {ns:"login"}),
			4: t('3-what-spouse', {ns:"login"})
		};

		if (securityQuestionState.status && securityQuestionState.status !== 200) {
			dispatch(setToastNotification(securityQuestionState.message as string ?? "Not allowed to access this resource", "error"));
		} else {
			if([1,2,3,4].includes(securityQuestionState["question_id"] as number)){
				setUserSecurityQuestion(optionArray[securityQuestionState["question_id"] as number] as string);
			} else {
				setUserSecurityQuestion(securityQuestionState.question_text as string);
			}
			
		}
	}, [securityQuestionState, dispatch, t]);

	useEffect(() => {
		if (userUpdateState.updated) {
			const userProfileId = window.localStorage.getItem("userProfileId");
			if (userUpdateState.email === "changed") {
				if (!userProfileId) {
					navigate("/amp/signout");
				} else {
					navigate(`/amp/signout/${userProfileId}/${userUpdateState.username as string}`);
				}
			} else {
				if (userProfileId) {
					setUserEmail("");
					setUserSecurityQuestion("");
					setUserLanguage("");
					dispatch(changeModalState("modal-fade-out"));
					dispatch(getUserInfo(userProfileId));
					dispatch(getSecurityQuestion(userProfileId));
					dispatch(changeModalContent());
				} else {
					dispatch(setToastNotification("Not allowed to access this resource", "error"));
				}
			}
		}
	}, [userUpdateState, navigate, dispatch]);

	const editAccount = () => {
		const ariaLabelText = t("edit-account.title") as string;
		if (modalContentAriaLabel !== ariaLabelText) {
			dispatch(changeModalContent(<EditAccount />, t("edit-account.title") as string, ariaLabelText));
		}
		if (!modalIsCloseable) {
			dispatch(changeIsCloseable(true));
		}
		dispatch(changeModalState("modal-fade-in"));
	}

	const editEnotif = () => {
		dispatch(changeModalContent(<EditEnotifcation isPreview={false} />, t("edit-account.enotiftitle") as string, "Test"));
		if (!modalIsCloseable) {
			dispatch(changeIsCloseable(true));
		}
		dispatch(changeModalState("modal-fade-in"));
	}

	return (
		<>
			<div className="page-container">
				<div className="profile-intro">
					<h2 className="profile-title">{t("profile-title") as string}</h2>
					<div className="profile-description">
						<p>{t("description.paragraph-0") as string}</p>
						<p><Trans defaults={t("description.paragraph-1") as string} components={{bold: <strong/>}}/></p>
						{!isAgent && <p><Trans defaults={t("description.paragraph-2") as string} components={{bold: <strong/>}}/></p>}
						<p><Trans defaults={t("description.paragraph-3") as string} components={{bold: <strong/>}}/>
							{/* Need Href For Accessibiltiy */}
							{/* eslint-disable-next-line */}
							<a onClick={() => { navigate("/amp/contact-us"); }}>{t("description.hyperlink-text") as string}</a>.
						</p>
					</div>
				</div>
				<div className="profile-settings">
					<BlueBoxTitle upper content={t("profile-settings-title") as string} />
					<div className="profile-setting-box-container">
						<div className="profile-setting-box">
							<p className="profile-setting-description">
								<Trans defaults={t("profile-settings-description") as string}>
									<strong></strong>
								</Trans>
							</p>
							<AmpLoadingSpinner loading={userDetailsLoading || securityQuestionLoading || userEmail === "" || userSecurityQuestion === "" || userLanguage === ""}>
								<table className="profile-setting-details-table" role="presentation">
									<tbody>
										<tr>
											<td>{t("profile-settings-details-table.email") as string}:</td>
											<td>{userEmail}</td>
										</tr>
										<tr>
											<td>{t("profile-settings-details-table.password") as string}:</td>
											<td>**********</td>
										</tr>
										<tr>
											<td>{t("profile-settings-details-table.security") as string}:</td>
											<td>{userSecurityQuestion}</td>
										</tr>
										<tr>
											<td>{t("profile-settings-details-table.language") as string}:</td>
											<td>{userLanguage === "en_EN" && t("4-english", {ns:"login"})}
												{userLanguage === "fr_CA" && t("4-french", {ns:"login"})}</td>
										</tr>
									</tbody>
								</table>
							</AmpLoadingSpinner>
							<p className="profile-setting-footer"><AmpButton onClick={editAccount} type="submit" text={t("profile-settings-button") as string} disabled={userDetailsLoading || securityQuestionLoading || userEmail === "" || userSecurityQuestion === "" || userLanguage === ""} /></p>
						</div>
					</div>
				</div>
				{!isAgent && (<>
					<div className="profile-settings">
						<BlueBoxTitle upper content={t("profile-enotif-title") as string} />
						<div className="profile-setting-box-container">
							<div className="profile-setting-box">
								<p className="profile-setting-description">
									<Trans defaults={t("profile-enotif-description") as string}>
										<strong></strong>
									</Trans>
								</p>
								<AmpLoadingSpinner loading={userDetailsLoading || securityQuestionLoading || userEmail === "" || userSecurityQuestion === "" || userLanguage === ""}>
									<EditEnotifcation isPreview />
								</AmpLoadingSpinner>
								<p className="profile-setting-footer"><AmpButton onClick={editEnotif} type="submit" text={t("profile-settings-button") as string} disabled={userDetailsLoading || securityQuestionLoading || userEmail === "" || userSecurityQuestion === "" || userLanguage === ""} /></p>
							</div>
						</div>
					</div>
				</>)}

				<div className="profile-properties">
					<BlueBoxTitle upper content={t("profile-properties-title") as string} />
					<PropertySelectionList showFullVersion={true} />
				</div>
			</div>
		</>
	)
}